import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Server, ChevronDown, Check, Lock, Cloud, HardDrive } from 'lucide-react';
import { isTauri } from '@tauri-apps/api/core';
import { useMockServerStore } from '../store/mockServerStore';
import { useCloudMockStore } from '../store/cloudMockStore';
import {
  startMockServer,
  stopMockServer,
  getMockStatus,
  buildMockDataset,
} from '../services/mockServerService';
import {
  getCloudMockStatus,
  enableCloudMock,
  disableCloudMock,
} from '../services/cloudMockService';
import { isBackendWorkspace } from '../utils/workspaceStorage';

const MockServerControl = ({
  collections,
  requestTabs = [],
  schemaTabs = [],
  schemas = null,
  showToast,
  currentWorkspace,
  isAuthenticated,
  user,
  onOpenSettings,
}) => {
  const { t } = useTranslation();
  const { running, port, setStatus, reset } = useMockServerStore();
  const { enabled: cloudEnabled, baseUrl: cloudBaseUrl, setStatus: setCloudStatus, reset: resetCloud } = useCloudMockStore();
  const [busy, setBusy] = useState(false);
  const [cloudBusy, setCloudBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const [available] = useState(() => {
    try { return isTauri(); } catch { return false; }
  });

  const isPro = isAuthenticated && user?.plan && user.plan !== 'free';
  const isCloudWs = isAuthenticated && isBackendWorkspace(currentWorkspace, isAuthenticated);

  // Sync local mock status on mount
  useEffect(() => {
    if (!available) return;
    getMockStatus().then(setStatus).catch(() => {});
  }, [available, setStatus]);

  // Sync cloud mock status when workspace changes
  useEffect(() => {
    if (!isCloudWs || !currentWorkspace?.id) { resetCloud(); return; }
    getCloudMockStatus(currentWorkspace.id).then(setCloudStatus).catch(() => {});
  }, [currentWorkspace?.id, isCloudWs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close when another titlebar dropdown opens
  useEffect(() => {
    const handler = (e) => { if (e.detail !== 'mock') setOpen(false); };
    window.addEventListener('titlebar-dropdown-open', handler);
    return () => window.removeEventListener('titlebar-dropdown-open', handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const handleToggleLocalMock = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (running) {
        await stopMockServer();
        reset();
      } else {
        const dataset = buildMockDataset({ collections, schemas }, requestTabs, schemaTabs);
        const status = await startMockServer(dataset);
        setStatus(status);
        showToast?.(t('mockServer.started', { port: status?.port ?? '' }), 'success');
      }
    } catch (err) {
      showToast?.(t('mockServer.error', { message: String(err?.message || err) }), 'error');
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }, [busy, running, collections, requestTabs, schemaTabs, schemas, setStatus, reset, showToast, t]);

  const handleCloudMockClick = useCallback(async () => {
    if (!isPro) {
      onOpenSettings?.('billing');
      setOpen(false);
      return;
    }
    if (cloudBusy || !currentWorkspace?.id) return;
    setCloudBusy(true);
    try {
      if (cloudEnabled) {
        const status = await disableCloudMock(currentWorkspace.id);
        setCloudStatus(status);
      } else {
        const status = await enableCloudMock(currentWorkspace.id);
        setCloudStatus(status);
        showToast?.(`Cloud Mock enabled at ${status.base_url}`, 'success');
      }
    } catch (err) {
      if (err.upgradeRequired) {
        onOpenSettings?.('billing');
      } else {
        showToast?.(err.message || 'Cloud Mock error', 'error');
      }
    } finally {
      setCloudBusy(false);
      setOpen(false);
    }
  }, [isPro, cloudBusy, cloudEnabled, currentWorkspace?.id, setCloudStatus, onOpenSettings, showToast]);

  if (!available) return null;

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) window.dispatchEvent(new CustomEvent('titlebar-dropdown-open', { detail: 'mock' }));
  };

  const anyRunning = running || cloudEnabled;

  return (
    <div ref={containerRef} className="env-picker mock-picker flex-shrink-0">
      <button
        type="button"
        onClick={handleOpen}
        className="env-picker__trigger h-7 rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-800 shadow-sm hover:bg-gray-50 transition-colors"
        title={running ? t('mockServer.runningOn', { port }) : cloudEnabled ? `Cloud Mock: ${cloudBaseUrl}` : t('mockServer.stopped')}
        aria-expanded={open}
        aria-haspopup="listbox"
        style={{ WebkitAppRegion: 'no-drag' }}
      >
        <Server size={14} style={{ color: anyRunning ? '#16a34a' : undefined, flexShrink: 0 }} />
        <span className="min-w-0 flex-1 truncate">Mock</span>
        <ChevronDown
          size={14}
          className={`env-picker__chevron text-gray-500 ${open ? 'env-picker__chevron--open' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          className="env-picker__menu rounded-lg border border-gray-200 bg-white shadow-lg"
          role="listbox"
        >
          <div className="env-picker__list">
            {/* Local Mock */}
            <button
              type="button"
              role="option"
              aria-selected={running}
              disabled={busy}
              onClick={handleToggleLocalMock}
              className={`env-picker__row ${running ? 'env-picker__row--active' : ''}`}
              title={running ? t('mockServer.runningOn', { port }) : t('mockServer.stopped')}
            >
              {running ? (
                <Check size={12} className="shrink-icon text-gray-700" />
              ) : (
                <span className="env-picker__row-spacer" aria-hidden />
              )}
              <HardDrive size={12} className="mock-picker__local-icon" aria-hidden />
              <span className="min-w-0 flex-1 truncate">Local Mock</span>
              {running && <span className="mock-picker__port">:{port}</span>}
            </button>

            {/* Cloud Mock — only show for authenticated users */}
            {isAuthenticated && (
              <button
                type="button"
                role="option"
                aria-selected={cloudEnabled}
                disabled={cloudBusy || (!isPro && false)}
                onClick={handleCloudMockClick}
                className={`env-picker__row ${cloudEnabled ? 'env-picker__row--active' : ''} ${!isPro ? 'mock-picker__row--locked' : ''}`}
                title={
                  !isPro
                    ? 'Cloud Mock requires a Pro plan'
                    : !isCloudWs
                    ? 'Cloud Mock is only available for cloud/team workspaces'
                    : cloudEnabled
                    ? `Running at ${cloudBaseUrl}`
                    : 'Start Cloud Mock'
                }
              >
                {cloudEnabled ? (
                  <Check size={12} className="shrink-icon text-gray-700" />
                ) : (
                  <span className="env-picker__row-spacer" aria-hidden />
                )}
                <Cloud size={12} className="mock-picker__cloud-icon" aria-hidden />
                <span className="min-w-0 flex-1 truncate">Cloud Mock</span>
                {!isPro && <span className="mock-picker__pro-badge">Pro</span>}
                {isPro && cloudEnabled && cloudBaseUrl && (
                  <span className="mock-picker__cloud-url" title={cloudBaseUrl}>
                    {cloudBaseUrl.replace('https://', '')}
                  </span>
                )}
                {!isPro && <Lock size={11} className="mock-picker__lock" aria-hidden />}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MockServerControl;
